import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper,
  Divider,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';

const TermsAndConditions = () => {
  const navigate = useNavigate();
  const { session } = useAuth();

  const handleBackClick = () => {
    if (session?.user) {
      // User is logged in, go to conversation page
      navigate('/conversation');
    } else {
      // User is not logged in, go to signup section of login page
      navigate('/login?mode=signup');
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBackClick}
          sx={{ mb: 2 }}
        >
          {session?.user ? 'Back to Conversation' : 'Back to Sign Up'}
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 'bold', color: '#1976d2' }}>
          Terms and Conditions
        </Typography>

        <Typography variant="body2" sx={{ mb: 4, fontStyle: 'italic', color: 'text.secondary' }}>
          Last Updated: {new Date().toLocaleDateString()}
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            1. Acceptance of Terms
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            By accessing and using Sanity Reps ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. 
            If you do not agree to abide by the above, please do not use this service. These Terms and Conditions constitute a legally binding 
            agreement between you and Sanity Reps regarding your use of our AI-powered wellness platform.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            2. Service Description and Limitations
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            Sanity Reps provides AI-powered wellness coaching, mental health support, and therapeutic guidance through conversational interfaces. 
            Our service includes but is not limited to: personalized meditation sessions, journal prompts, mood tracking, progress monitoring, 
            and AI-generated therapeutic responses based on established psychological frameworks.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6, fontWeight: 'bold', color: 'error.main' }}>
            IMPORTANT DISCLAIMER: This service is NOT a substitute for professional medical care, diagnosis, or treatment. Our AI wellness 
            representatives are designed to provide supportive guidance and are not licensed therapists, psychologists, or medical professionals. 
            If you are experiencing a mental health crisis, suicidal thoughts, or require immediate medical attention, please contact emergency 
            services (911), the National Suicide Prevention Lifeline (988), or seek immediate professional help.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            3. Privacy and Confidentiality
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            We understand the sensitive nature of mental health conversations and are committed to maintaining the confidentiality of your 
            communications within the bounds of our technical capabilities and legal requirements. Your conversations with our AI wellness 
            representatives are treated with the utmost care and are protected by industry-standard encryption both in transit and at rest.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            However, please be aware that while we strive to maintain confidentiality, absolute confidentiality cannot be guaranteed in 
            digital communications. We may be required to disclose information if legally compelled to do so by law enforcement, court orders, 
            or if we believe in good faith that disclosure is necessary to prevent imminent harm to yourself or others.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            Your data is stored securely and is never sold to third parties for marketing purposes. We may use aggregated, anonymized data 
            for research and service improvement purposes, but individual conversations remain private and are not shared without your explicit consent.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            4. Third-Party Services and APIs
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            Our service utilizes various third-party APIs and services to provide you with the best possible experience, including but not limited to:
          </Typography>
          <Typography component="ul" sx={{ ml: 3, mb: 2, lineHeight: 1.6 }}>
            <li>OpenAI's GPT models for conversational AI and text generation</li>
            <li>Text-to-speech services for voice interactions</li>
            <li>Cloud hosting and database services (Supabase, AWS, Google Cloud, etc.)</li>
            <li>Payment processing services (Stripe) for subscription management</li>
            <li>Analytics and performance monitoring tools</li>
            <li>Email delivery services for account notifications</li>
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            These third-party services have their own terms of service and privacy policies. While we carefully select our partners and 
            ensure they meet our security standards, we cannot be held responsible for the practices of these external services. 
            By using our service, you acknowledge that your data may be processed by these third-party providers in accordance with 
            their respective terms and policies.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            5. User Responsibilities and Prohibited Uses
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            You agree to use our service responsibly and in accordance with all applicable laws. You are prohibited from:
          </Typography>
          <Typography component="ul" sx={{ ml: 3, mb: 2, lineHeight: 1.6 }}>
            <li>Using the service for any illegal, harmful, or malicious purposes</li>
            <li>Attempting to circumvent security measures or access unauthorized features</li>
            <li>Sharing your account credentials with others</li>
            <li>Using the service to harass, threaten, or harm others</li>
            <li>Providing false or misleading information during registration</li>
            <li>Attempting to reverse engineer, modify, or create derivative works of our software</li>
            <li>Using automated tools or bots to interact with our service</li>
            <li>Violating any third-party rights or terms of service</li>
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            6. Intellectual Property Rights
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            All content, features, and functionality of the Sanity Reps service, including but not limited to text, graphics, logos, 
            icons, images, audio clips, digital downloads, data compilations, and software, are the exclusive property of Sanity Reps 
            or its licensors and are protected by United States and international copyright, trademark, patent, trade secret, and other 
            intellectual property laws.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            You retain ownership of any personal information and content you provide to our service. However, by using our service, 
            you grant us a limited, non-exclusive, royalty-free license to use, process, and analyze your data solely for the purpose 
            of providing and improving our services.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            7. Limitation of Liability and Disclaimers
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6, fontWeight: 'bold', color: 'error.main' }}>
            TO THE FULLEST EXTENT PERMITTED BY LAW, SANITY REPS AND ITS AFFILIATES, DIRECTORS, EMPLOYEES, AGENTS, SUPPLIERS, 
            AND LICENSORS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING 
            WITHOUT LIMITATION DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR 
            USE OF THE SERVICE.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            Our service is provided "AS IS" and "AS AVAILABLE" without any warranties of any kind, either express or implied. 
            We do not warrant that the service will be uninterrupted, error-free, or completely secure. The AI responses provided 
            by our service are generated based on algorithms and training data and may not always be accurate, appropriate, or 
            suitable for your specific situation.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            You acknowledge that your use of our AI wellness service is at your own risk and that you should always consult with 
            qualified healthcare professionals for serious mental health concerns.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            8. Subscription and Payment Terms
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            Our service may be offered through various subscription plans with different features and pricing. All subscription fees 
            are billed in advance and are non-refundable except as required by law or as specifically stated in our refund policy. 
            Subscription fees are subject to change with appropriate notice.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            You may cancel your subscription at any time through your account settings. Cancellation will be effective at the end 
            of your current billing period. We reserve the right to suspend or terminate accounts for non-payment or violation of 
            these terms.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            9. Data Security and Breach Notification
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            We implement industry-standard security measures to protect your personal information, including encryption, secure servers, 
            and regular security audits. However, no method of transmission over the internet or electronic storage is 100% secure, 
            and we cannot guarantee absolute security.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            In the event of a data breach that may compromise your personal information, we will notify affected users and relevant 
            authorities as required by applicable law, typically within 72 hours of discovery.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            10. Geographic Restrictions and Compliance
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            Our service is intended for users in the United States and is governed by U.S. law. We make no representation that 
            our service is appropriate or available for use in other jurisdictions. Users accessing the service from other countries 
            do so at their own risk and are responsible for compliance with local laws.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            11. Modifications to Terms
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting 
            on our website. Your continued use of the service after any changes constitutes acceptance of the new terms. We will make 
            reasonable efforts to notify users of significant changes to these terms.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            12. Termination
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            We may terminate or suspend your account and access to the service immediately, without prior notice, for any reason, 
            including but not limited to violation of these Terms and Conditions. Upon termination, your right to use the service 
            will cease immediately, and we may delete your account and all associated data.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            You may terminate your account at any time by contacting our support team or through your account settings. Termination 
            does not relieve you of any obligations accrued prior to termination.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            13. Governing Law and Dispute Resolution
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            These Terms and Conditions shall be governed by and construed in accordance with the laws of the United States and the 
            state in which our company is incorporated, without regard to its conflict of law principles. Any disputes arising under 
            these terms shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            14. Severability
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            If any provision of these Terms and Conditions is found to be unenforceable or invalid, that provision will be limited 
            or eliminated to the minimum extent necessary so that these terms will otherwise remain in full force and effect.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            15. Contact Information
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            If you have any questions about these Terms and Conditions, please contact us at:
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.6 }}>
            Email: legal@sanityreps.com
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.6 }}>
            Address: [Company Address]
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            Phone: [Company Phone Number]
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 2, p: 3, bgcolor: 'grey.100', borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Acknowledgment
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
            By clicking "I agree to the Terms and Conditions" during account registration, you acknowledge that you have read, 
            understood, and agree to be bound by these Terms and Conditions. You also acknowledge that you understand the limitations 
            of our AI-powered service and will seek professional help when appropriate.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default TermsAndConditions;
